import type { AppConfig } from '../state/config.ts';
import { loadUserSources } from '../state/sourceCache.ts';
import { clearAccountCache, lsGet, lsRemove, lsSet } from '../state/storage.ts';
import { GUEST_PERSISTENCE, type Store, type StorePersistence } from '../state/store.ts';
import { AccountAuth } from './client.ts';
import {
  configFromProfile,
  profileFromConfig,
  cacheAccountSnapshot,
  loadAccountDraftOverrides,
  saveAccountDraftOverrides,
} from './localCache.ts';
import { loadAccountSnapshotWithCache, SupabaseSyncRepository } from './sync.ts';
import type {
  AccountRuntimeConfig,
  AccountSnapshot,
  AuthState,
  SleeperProfile,
  SyncStatus,
} from './types.ts';
import { SyncConflictError, validateSnapshot } from './validation.ts';
import type { RankingSource } from '../core/types.ts';

const ACTIVE_ACCOUNT_KEY = 'active_account_id';

export interface MergeConflict {
  kind: 'profile' | 'source';
  key: string;
  deviceLabel: string;
  accountLabel: string;
}

export interface PendingSync {
  device: AccountSnapshot;
  account: AccountSnapshot;
  conflicts: MergeConflict[];
  reason: 'initial' | 'conflict';
}

export interface AccountManagerState {
  auth: AuthState;
  sync: SyncStatus;
  snapshot: AccountSnapshot;
  pending: PendingSync | null;
  error: string | null;
}

type Listener = (state: AccountManagerState) => void;

export class AccountManager {
  readonly auth = new AccountAuth();
  private store: Store | null = null;
  private runtime: AccountRuntimeConfig = {
    enabled: false,
    supabase_url: null,
    supabase_publishable_key: null,
    production_origin: null,
    turnstile_site_key: null,
  };
  private repository: SupabaseSyncRepository | null = null;
  private activeUserId: string | null = null;
  private guestConfig: AppConfig | null = null;
  private guestSources: RankingSource[] = [];
  private profileTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private dirtyProfiles = new Map<string, { profile: SleeperProfile; expectedUpdatedAt: string | null }>();
  private dirtySources: RankingSource[] | null = null;
  private sessionGeneration = 0;
  private cleanupPromise: Promise<void> | null = null;
  private listeners = new Set<Listener>();
  private state: AccountManagerState = {
    auth: { status: 'loading' },
    sync: 'loading',
    snapshot: { profiles: [], sources: [], source_versions: {} },
    pending: null,
    error: null,
  };

  getState(): AccountManagerState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  async initialize(runtime: AccountRuntimeConfig, store: Store): Promise<void> {
    this.runtime = runtime;
    this.store = store;
    if (typeof window !== 'undefined') window.addEventListener('online', this.retryDirty);
    this.auth.subscribe((auth) => {
      this.setState({ auth });
      if (auth.status === 'signed-in') void this.connect(auth.user_id, ++this.sessionGeneration);
      if (auth.status === 'signed-out') void this.cleanupSignedOut(auth.error ?? null);
      if (auth.status === 'disabled') this.setState({ sync: 'disabled' });
    });
    await this.auth.initialize(runtime);
  }

  async sendMagicLink(email: string, captchaToken?: string | null): Promise<void> {
    await this.auth.sendMagicLink(email, captchaToken);
  }

  async commitInitialSync(choice: 'device' | 'account' | 'merge', decisions = new Map<string, 'device' | 'account'>()): Promise<void> {
    const pending = this.state.pending;
    if (!pending || !this.repository || !this.activeUserId) return;

    try {
      let snapshot: AccountSnapshot;
      if (choice === 'device') {
        snapshot = pending.device;
        snapshot = await this.repository.replaceSnapshot(snapshot);
      } else if (choice === 'account') {
        snapshot = pending.account;
      } else {
        snapshot = mergeSnapshots(pending.device, pending.account, decisions);
        validateSnapshot(snapshot);
        snapshot = await this.repository.replaceSnapshot(snapshot);
      }
      await this.activateSnapshot(snapshot);
    } catch (error) {
      this.setState({ sync: 'error', error: errorMessage(error) });
      throw error;
    }
  }

  async signOut(): Promise<void> {
    await this.auth.signOut();
    await this.cleanupSignedOut(null);
  }

  async deleteAccount(): Promise<void> {
    await this.auth.deleteAccount();
    await this.cleanupSignedOut(null);
  }

  profileForUsername(username: string): SleeperProfile | null {
    return this.state.snapshot.profiles.find((profile) => profile.sleeper_username === username) ?? null;
  }

  selectProfile(profile: SleeperProfile): void {
    if (!this.store || !this.activeUserId) return;
    this.store.setPersistence(this.accountPersistence());
    this.store.resetToOnboarding(configFromProfile(profile, this.guestConfig ?? this.store.get().config));
  }

  private async connect(userId: string, generation = this.sessionGeneration): Promise<void> {
    if (this.activeUserId === userId && this.repository) return;
    if (!this.store || !this.auth.getClient()) return;

    this.activeUserId = userId;
    this.guestConfig ??= this.store.get().config;
    this.guestSources = await loadUserSources();
    this.repository = new SupabaseSyncRepository(this.auth.getClient()!, userId);
    this.setState({ sync: 'loading', error: null });

    const loaded = await loadAccountSnapshotWithCache(userId, this.repository);
    if (generation !== this.sessionGeneration || this.activeUserId !== userId || this.state.auth.status !== 'signed-in') return;
    const device = {
      profiles: this.guestConfig ? [profileFromConfig(this.guestConfig)].filter((p): p is SleeperProfile => p !== null) : [],
      sources: this.guestSources,
      source_versions: {},
    };
    const account = loaded.snapshot;
    this.repository.setCachedSourceVersions?.(account.source_versions);
    this.setState({ snapshot: account, sync: loaded.fromCache ? 'offline' : 'synced' });

    if (lsGet<string | null>(ACTIVE_ACCOUNT_KEY, null) === userId) {
      await this.activateSnapshot(account);
      return;
    }

    const pending = { device, account, conflicts: findConflicts(device, account), reason: 'initial' as const };
    if (meaningful(device)) {
      this.setState({ pending });
      return;
    }
    await this.activateSnapshot(account);
  }

  private async activateSnapshot(snapshot: AccountSnapshot): Promise<void> {
    if (!this.store || !this.activeUserId) return;
    validateSnapshot(snapshot);
    const sync = this.state.sync === 'offline' ? 'offline' : 'synced';
    this.state = { ...this.state, snapshot, pending: null, sync, error: null };
    this.notify();
    lsSet(ACTIVE_ACCOUNT_KEY, this.activeUserId);
    await cacheAccountSnapshot(this.activeUserId, snapshot);
    this.store.setPersistence(this.accountPersistence());

    const currentUsername = this.store.get().config.sleeper_username;
    const profile =
      snapshot.profiles.find((item) => item.sleeper_username === currentUsername) ??
      [...snapshot.profiles].sort((a, b) => (b.last_used_at ?? '').localeCompare(a.last_used_at ?? ''))[0];
    const config = profile ? configFromProfile(profile, this.guestConfig ?? this.store.get().config) : this.store.get().config;
    this.store.resetToOnboarding(config);
  }

  private accountPersistence(): StorePersistence {
    return {
      loadSources: async () => this.state.snapshot.sources,
      loadAvailabilityOverrides: (draftId) =>
        this.activeUserId ? loadAccountDraftOverrides(this.activeUserId, draftId) : [],
      saveAvailabilityOverrides: (draftId, overrides) => {
        if (this.activeUserId) saveAccountDraftOverrides(this.activeUserId, draftId, overrides);
      },
      saveSources: (sources) => {
        if (!this.activeUserId || !this.repository) return;
        // Built-in feeds are recreated by the source registry and must never
        // enter the account database. Only user-authored imports are synced.
        const syncedSources = sources.filter((source) => source.kind === 'user');
        const next = { ...this.state.snapshot, sources: syncedSources };
        this.state = { ...this.state, snapshot: next };
        this.notify();
        void cacheAccountSnapshot(this.activeUserId, next);
        this.dirtySources = syncedSources;
        void this.flushSources(syncedSources);
      },
      saveConfig: (config) => {
        if (!this.activeUserId || !this.repository) return;
        const profile = profileFromConfig(config);
        if (!profile) return;
        const priorProfile = this.state.snapshot.profiles.find((item) => item.sleeper_user_id === profile.sleeper_user_id);
        const pendingProfile = { ...profile, updated_at: priorProfile?.updated_at ?? null };
        const profiles = [
          ...this.state.snapshot.profiles.filter((item) => item.sleeper_user_id !== profile.sleeper_user_id),
          pendingProfile,
        ];
        const next = { ...this.state.snapshot, profiles };
        this.state = { ...this.state, snapshot: next };
        this.notify();
        void cacheAccountSnapshot(this.activeUserId, next);
        this.dirtyProfiles.set(profile.sleeper_user_id, {
          profile: pendingProfile,
          expectedUpdatedAt: priorProfile?.updated_at ?? null,
        });

        const prior = this.profileTimers.get(profile.sleeper_user_id);
        if (prior) clearTimeout(prior);
        const timer = setTimeout(() => {
          this.profileTimers.delete(profile.sleeper_user_id);
          void this.flushProfile(profile.sleeper_user_id);
        }, 750);
        this.profileTimers.set(profile.sleeper_user_id, timer);
      },
    };
  }

  private async flushSources(sources: RankingSource[]): Promise<void> {
    if (!this.repository || !this.activeUserId) return;
    try {
      const source_versions = await this.repository.saveSources(sources);
      // A newer local edit may have started while this request was in flight.
      // Do not let the older response roll the optimistic snapshot backward.
      if (this.dirtySources !== sources) return;
      this.dirtySources = null;
      const next = { ...this.state.snapshot, sources, source_versions };
      this.setState({ snapshot: next, sync: 'synced', error: null });
      if (this.activeUserId) void cacheAccountSnapshot(this.activeUserId, next);
    } catch (error: unknown) {
      if (error instanceof SyncConflictError) {
        await this.enterConflict();
      } else {
        this.setState({ sync: this.isOffline() ? 'offline' : 'error', error: errorMessage(error) });
      }
    }
  }

  private async flushProfile(userId: string): Promise<void> {
    const pending = this.dirtyProfiles.get(userId);
    if (!pending || !this.repository || !this.activeUserId) return;
    try {
      const saved = await this.repository.saveProfile(pending.profile, pending.expectedUpdatedAt);
      if (this.dirtyProfiles.get(userId) !== pending) return;
      this.dirtyProfiles.delete(userId);
      const profiles = this.state.snapshot.profiles.map((item) => item.sleeper_user_id === saved.sleeper_user_id ? saved : item);
      const next = { ...this.state.snapshot, profiles };
      this.setState({ snapshot: next, sync: 'synced', error: null });
      if (this.activeUserId) void cacheAccountSnapshot(this.activeUserId, next);
    } catch (error: unknown) {
      if (error instanceof SyncConflictError) {
        await this.enterConflict();
      } else {
        this.setState({ sync: this.isOffline() ? 'offline' : 'error', error: errorMessage(error) });
      }
    }
  }

  private async enterConflict(): Promise<void> {
    if (!this.repository || !this.activeUserId || this.state.pending || this.state.sync === 'conflict') return;
    const device = this.state.snapshot;
    this.clearDirtyState();
    try {
      const account = await this.repository.loadSnapshot({ cache: false });
      if (!this.activeUserId) return;
      this.setState({
        pending: { device, account, conflicts: findConflicts(device, account), reason: 'conflict' },
        sync: 'conflict',
        error: 'Saved account data changed on another device. Choose which version to keep.',
      });
    } catch (error: unknown) {
      this.setState({ sync: this.isOffline() ? 'offline' : 'error', error: errorMessage(error) });
    }
  }

  private retryDirty = (): void => {
    void this.flushDirty();
  };

  private async flushDirty(): Promise<void> {
    if (this.dirtySources) await this.flushSources(this.dirtySources);
    for (const userId of this.dirtyProfiles.keys()) await this.flushProfile(userId);
  }

  private clearDirtyState(): void {
    for (const timer of this.profileTimers.values()) clearTimeout(timer);
    this.profileTimers.clear();
    this.dirtyProfiles.clear();
    this.dirtySources = null;
  }

  private async cleanupSignedOut(error: string | null): Promise<void> {
    if (this.cleanupPromise) return this.cleanupPromise;
    this.sessionGeneration += 1;
    const userId = this.activeUserId;
    this.clearDirtyState();
    this.activeUserId = null;
    this.repository = null;
    this.cleanupPromise = (async () => {
      if (userId) await clearAccountCache(userId);
      lsRemove(ACTIVE_ACCOUNT_KEY);
      this.state = {
        auth: { status: 'signed-out', ...(error ? { error } : {}) },
        sync: 'signed-out',
        snapshot: { profiles: [], sources: [], source_versions: {} },
        pending: null,
        error,
      };
      this.notify();
      if (this.store && this.guestConfig) {
        this.store.setPersistence(GUEST_PERSISTENCE);
        this.store.resetToOnboarding(this.guestConfig);
      }
    })().finally(() => {
      this.cleanupPromise = null;
    });
    return this.cleanupPromise;
  }

  private isOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  private setState(patch: Partial<AccountManagerState>): void {
    this.state = { ...this.state, ...patch };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) listener(this.state);
  }
}

function meaningful(snapshot: AccountSnapshot): boolean {
  return snapshot.profiles.length > 0 || snapshot.sources.length > 0;
}

export function findConflicts(device: AccountSnapshot, account: AccountSnapshot): MergeConflict[] {
  const conflicts: MergeConflict[] = [];
  const accountProfiles = new Map(account.profiles.map((profile) => [profile.sleeper_user_id, profile]));
  for (const profile of device.profiles) {
    const other = accountProfiles.get(profile.sleeper_user_id);
    if (other) conflicts.push({ kind: 'profile', key: profile.sleeper_user_id, deviceLabel: profile.sleeper_username, accountLabel: other.sleeper_username });
  }
  const accountSources = new Map(account.sources.map((source) => [source.source_id, source]));
  for (const source of device.sources) {
    const other = accountSources.get(source.source_id);
    if (other) conflicts.push({ kind: 'source', key: source.source_id, deviceLabel: source.source_name, accountLabel: other.source_name });
  }
  return conflicts;
}

export function mergeSnapshots(
  device: AccountSnapshot,
  account: AccountSnapshot,
  decisions: Map<string, 'device' | 'account'>,
): AccountSnapshot {
  const profiles = new Map(account.profiles.map((profile) => [profile.sleeper_user_id, profile]));
  for (const profile of device.profiles) {
    const key = `profile:${profile.sleeper_user_id}`;
    if (!profiles.has(profile.sleeper_user_id) || decisions.get(key) === 'device') profiles.set(profile.sleeper_user_id, profile);
  }

  const sources = new Map(account.sources.map((source) => [source.source_id, source]));
  const source_versions = { ...account.source_versions };
  for (const source of device.sources) {
    const key = `source:${source.source_id}`;
    if (!sources.has(source.source_id) || decisions.get(key) === 'device') {
      sources.set(source.source_id, source);
      source_versions[source.source_id] = device.source_versions[source.source_id] ?? null;
    }
  }
  return { profiles: [...profiles.values()], sources: [...sources.values()], source_versions };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Sync failed. Your local copy is still available.';
}
